"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  resolveTooltipPosition,
  type TooltipPosition,
} from "./tooltip-position";
import { FROSTED_TOOLTIP_SURFACE_CLASS } from "./tooltip";

const INTERACTIVE =
  'a[href],button,input,select,textarea,[role="button"],[role="checkbox"],[role="menuitem"],[role="option"],[role="radio"],[role="switch"],[tabindex]:not([tabindex="-1"])';
const BRIDGED_TITLE = "data-fintheon-tooltip-title";

type ActiveTitle = { element: HTMLElement; text: string };

/**
 * Migrates existing interactive `title` help at the application edge. Semantic
 * document and component titles remain untouched because they are not controls.
 */
export function NativeTitleTooltipBridge() {
  const [active, setActive] = useState<ActiveTitle | null>(null);
  const [position, setPosition] = useState<TooltipPosition | null>(null);
  const activeRef = useRef<ActiveTitle | null>(null);
  const panelRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  const normalize = useCallback((element: HTMLElement) => {
    if (!isInteractiveTitle(element)) return null;
    const title = element.getAttribute("title")?.trim();
    const saved = element.getAttribute(BRIDGED_TITLE)?.trim();
    const text = title || saved;
    if (!text) return null;
    if (!element.getAttribute("aria-label") && !element.textContent?.trim()) {
      element.setAttribute("aria-label", text);
    }
    element.setAttribute(BRIDGED_TITLE, text);
    if (title) element.removeAttribute("title");
    return text;
  }, []);

  const place = useCallback(() => {
    const element = active?.element;
    const panel = panelRef.current;
    if (!element?.isConnected || !panel) return;
    setPosition(
      resolveTooltipPosition(
        element.getBoundingClientRect(),
        { height: panel.offsetHeight || 1, width: panel.offsetWidth || 1 },
        "top",
        { height: window.innerHeight, width: window.innerWidth },
      ),
    );
  }, [active]);

  useLayoutEffect(() => {
    if (typeof document === "undefined") return;
    for (const element of document.querySelectorAll<HTMLElement>("[title]")) {
      normalize(element);
    }
    const observer = new MutationObserver((changes) => {
      for (const change of changes) {
        if (
          change.type === "attributes" &&
          change.target instanceof HTMLElement
        )
          normalize(change.target);
        for (const node of change.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          normalize(node);
          for (const element of node.querySelectorAll<HTMLElement>("[title]"))
            normalize(element);
        }
      }
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["title"],
      childList: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, [normalize]);

  useEffect(() => {
    const open = (event: Event) => {
      const element = interactiveAncestor(event.target);
      const text = element ? normalize(element) : null;
      if (element && text) {
        setPosition(null);
        setActive({ element, text });
      }
    };
    const close = (event: Event) => {
      const current = activeRef.current;
      if (interactiveAncestor(event.target) !== current?.element) return;
      const related = (event as FocusEvent | PointerEvent).relatedTarget;
      if (current.element.contains(related as Node | null)) return;
      setActive(null);
      setPosition(null);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setActive(null);
        setPosition(null);
      }
    };
    document.addEventListener("pointerover", open, true);
    document.addEventListener("focusin", open, true);
    document.addEventListener("pointerout", close, true);
    document.addEventListener("focusout", close, true);
    document.addEventListener("keydown", escape, true);
    return () => {
      document.removeEventListener("pointerover", open, true);
      document.removeEventListener("focusin", open, true);
      document.removeEventListener("pointerout", close, true);
      document.removeEventListener("focusout", close, true);
      document.removeEventListener("keydown", escape, true);
    };
  }, [normalize]);

  useLayoutEffect(() => {
    if (active) place();
  }, [active, place]);

  useEffect(() => {
    if (!active) return;
    const observer =
      typeof ResizeObserver === "undefined" || !panelRef.current
        ? null
        : new ResizeObserver(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    if (panelRef.current) observer?.observe(panelRef.current);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      observer?.disconnect();
    };
  }, [active, place]);

  if (!(active && typeof document !== "undefined")) return null;

  return createPortal(
    <span
      aria-hidden={position ? undefined : true}
      className="pointer-events-none fixed z-[9999]"
      style={{
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? "visible" : "hidden",
      }}
    >
      <span
        ref={panelRef}
        role="tooltip"
        className={FROSTED_TOOLTIP_SURFACE_CLASS}
      >
        {active.text}
      </span>
    </span>,
    document.body,
  );
}

function interactiveAncestor(target: EventTarget | null) {
  return target instanceof Element
    ? (target.closest(INTERACTIVE) as HTMLElement | null)
    : null;
}

function isInteractiveTitle(element: HTMLElement) {
  return (
    element.matches(INTERACTIVE) &&
    !element.hasAttribute("data-title-semantic") &&
    !element.hasAttribute("data-tooltip-title-exempt")
  );
}
